import React, { useState, useEffect, useRef } from 'react'
import { Button, Col, Form, Row, Card } from 'react-bootstrap'
import 'bootstrap/dist/css/bootstrap.min.css'
import ApiEndpoints, {BASE_URL} from './commonComponents/ApiEndpoints'
import toast from 'react-hot-toast'
import axios from 'axios'
import Cookies from 'js-cookie'
import {
  BsFunnelFill,
  BsSearch,
  BsArrowClockwise,
  BsCalendar3,
  BsFiletypePdf,
} from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'
import { useNavigate, useParams } from 'react-router-dom'
import { Typeahead } from 'react-bootstrap-typeahead'
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table'
import html2pdf from 'html2pdf.js'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'

function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

async function fetchImageAsDataUrlForPdf(url) {
  if (!url || String(url).startsWith('data:')) return url

  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit' })
    if (res.ok) {
      return await blobToDataURL(await res.blob())
    }
  } catch {
    // Direct cross-origin fetch failed; try proxies below.
  }

  try {
    const proxiedUrl = `https://images.weserv.nl/?url=${encodeURIComponent(url)}`
    const res = await fetch(proxiedUrl)
    if (res.ok) {
      return await blobToDataURL(await res.blob())
    }
  } catch {
    // Try one more proxy.
  }

  try {
    const proxiedUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`
    const res = await fetch(proxiedUrl)
    if (res.ok) {
      return await blobToDataURL(await res.blob())
    }
  } catch {
    // Ignore and fall through.
  }

  return null
}

async function makeWatermarkImageDataUrl(sourceDataUrl, size = 600, opacity = 0.08) {
  if (!sourceDataUrl) return null

  try {
    return await new Promise((resolve, reject) => {
      const imageEl = new Image()
      imageEl.onload = () => {
        try {
          const canvas = document.createElement('canvas')
          canvas.width = size
          canvas.height = size
          const ctx = canvas.getContext('2d')
          const minSide = Math.min(imageEl.naturalWidth, imageEl.naturalHeight)
          const sx = (imageEl.naturalWidth - minSide) / 2
          const sy = (imageEl.naturalHeight - minSide) / 2

          ctx.clearRect(0, 0, size, size)
          ctx.save()
          ctx.beginPath()
          ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2)
          ctx.closePath()
          ctx.clip()
          ctx.globalAlpha = opacity
          ctx.drawImage(imageEl, sx, sy, minSide, minSide, 0, 0, size, size)
          ctx.restore()

          resolve(canvas.toDataURL('image/png'))
        } catch (e) {
          reject(e)
        }
      }
      imageEl.onerror = reject
      imageEl.src = sourceDataUrl
    })
  } catch {
    return sourceDataUrl
  }
}

const getSessionName = () =>
  Cookies.get('session_name') || Cookies.get('session') || ''

const normalizeApiDate = (date) => {
  if (!date) return ''
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)) {
    return date.slice(0, 10)
  }
  const parsedDate = new Date(date)
  if (Number.isNaN(parsedDate.getTime())) {
    return String(date).split('T')[0] || ''
  }
  const year = parsedDate.getFullYear()
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0')
  const day = String(parsedDate.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const getHolidayListFromResponse = (responseData) => {
  if (Array.isArray(responseData?.data)) return responseData.data
  if (responseData?.data && typeof responseData.data === 'object') {
    return [responseData.data]
  }
  if (Array.isArray(responseData?.holidays)) return responseData.holidays
  if (Array.isArray(responseData?.holiday)) return responseData.holiday
  if (responseData?.holiday && typeof responseData.holiday === 'object') {
    return [responseData.holiday]
  }
  return []
}

const isStudentHoliday = (holiday) => {
  const holidayType = String(
    holiday?.holiday_type || holiday?.type || '',
  ).toUpperCase()
  return !holidayType || holidayType === 'ALL' || holidayType === 'STUDENT'
}

const mapStudentAttendanceRow = (
  att,
  holidayDates = new Set(),
  holidayTitleByDate = {},
  topLevelHolidayTitle = '',
) => {
  const date = normalizeApiDate(att.date)
  const isHoliday = holidayDates.has(date)
  const status = isHoliday ? 'holiday' : att.status
  const isHolidayStatus = String(status || '').toLowerCase() === 'holiday'

  let remarks = att.remarks
  if (isHolidayStatus) {
    remarks =
      holidayTitleByDate[date] ||
      topLevelHolidayTitle ||
      att.holiday_title ||
      att.remarks
  }

  return {
    ...att,
    date: date || att.date,
    status,
    remarks,
  }
}

const addMissingStudentHolidayRows = (
  rows,
  holidays,
  students,
  selectedStudentId,
) => {
  const targetStudents = selectedStudentId
    ? students.filter(
        (student) => Number(student.id) === Number(selectedStudentId),
      )
    : students

  const existingRows = new Set(
    rows.map(
      (row) => `${Number(row.student_id)}-${normalizeApiDate(row.date)}`,
    ),
  )

  const holidayTitleByDate = {}
  holidays.filter(isStudentHoliday).forEach((holiday) => {
    const date = normalizeApiDate(holiday?.date)
    if (date && !holidayTitleByDate[date]) {
      holidayTitleByDate[date] = holiday.title || ''
    }
  })

  const holidayDates = Array.from(new Set(Object.keys(holidayTitleByDate)))
  const missingRows = []

  holidayDates.forEach((date) => {
    targetStudents.forEach((student) => {
      const rowKey = `${Number(student.id)}-${date}`
      if (existingRows.has(rowKey)) return

      existingRows.add(rowKey)
      missingRows.push({
        id: `holiday-${student.id}-${date}`,
        student_id: student.id,
        student_name: student.name || '-',
        admission_number: student.admission_number,
        roll_number:
          student.roll_number || student.roll_no || student.rollNumber || '',
        date,
        status: 'holiday',
        remarks: holidayTitleByDate[date] || 'Holiday',
      })
    })
  })

  return [...rows, ...missingRows]
}

const fetchStudentApplicableHolidays = async () => {
  const session = getSessionName()
  if (!session) return []

  const [studentRes, allRes] = await Promise.all([
    ApiEndpoints.getHolidayList(session, 'STUDENT').catch(() => null),
    ApiEndpoints.getHolidayList(session, 'ALL').catch(() => null),
  ])

  const merged = new Map()
  ;[
    ...getHolidayListFromResponse(allRes?.data),
    ...getHolidayListFromResponse(studentRes?.data),
  ]
    .filter(isStudentHoliday)
    .forEach((holiday) => {
      const key = [
        normalizeApiDate(holiday?.date),
        holiday?.title || '',
        holiday?.id ?? '',
      ].join('-')
      if (!merged.has(key)) merged.set(key, holiday)
    })

  return Array.from(merged.values())
}

const enrichStudentAttendanceWithHolidays = async ({
  attendanceList = [],
  students = [],
  selectedStudentId = null,
  topLevelHolidayTitle = '',
}) => {
  const holidays = await fetchStudentApplicableHolidays()
  const holidayTitleByDate = {}
  holidays.forEach((holiday) => {
    const date = normalizeApiDate(holiday?.date)
    if (date && !holidayTitleByDate[date]) {
      holidayTitleByDate[date] = holiday.title || ''
    }
  })
  const holidayDates = new Set(Object.keys(holidayTitleByDate))

  const mappedRows = (attendanceList || []).map((att) =>
    mapStudentAttendanceRow(
      att,
      holidayDates,
      holidayTitleByDate,
      topLevelHolidayTitle,
    ),
  )

  return addMissingStudentHolidayRows(
    mappedRows,
    holidays,
    students,
    selectedStudentId,
  )
}

const mapCoursesFromTeacherClasses = (payload) => {
  const raw = payload?.data ?? payload
  let list = []
  if (Array.isArray(raw)) {
    // Either a flat class list, or teacher rows that each contain assigned_classes
    list = raw.flatMap((item) => {
      const nested =
        item?.assigned_classes ?? item?.assignedClasses ?? item?.classes
      if (Array.isArray(nested)) return nested
      return [item]
    })
  } else if (raw && typeof raw === 'object') {
    list =
      raw.assigned_classes ?? raw.assignedClasses ?? raw.classes ?? []
  }

  const coursesList = []
  const seen = new Set()
  ;(Array.isArray(list) ? list : []).forEach((item) => {
    const id = item?.course_id ?? item?.courseId ?? item?.course?.id
    const name =
      item?.course_name ??
      item?.courseName ??
      (typeof item?.course === 'string' ? item.course : item?.course?.name)
    if (id == null || id === '' || !name) return
    const key = String(id)
    if (seen.has(key)) return
    seen.add(key)
    coursesList.push({ id: Number(id) || id, name })
  })
  return coursesList
}

const getTeacherAuthFromCookie = () => {
  const userCookie = Cookies.get('user')
  let token = null
  let isTeacher = false
  let teacherId = null
  if (userCookie) {
    try {
      const parsed = JSON.parse(userCookie)
      token = parsed?.token || null
      teacherId = parsed?.teacher_id ?? parsed?.id ?? null
      const userType = String(parsed?.user_type || '').toLowerCase()
      const roles = Array.isArray(parsed?.roles)
        ? parsed.roles.map((r) =>
            String(typeof r === 'object' && r != null ? r.name || r : r).toLowerCase(),
          )
        : []
      isTeacher = userType === 'teacher' || roles.includes('teacher')
    } catch (error) {
      console.error('Failed to parse user cookie', error)
    }
  }
  return { token, isTeacher, teacherId }
}

const fetchCoursesForViewAttendance = async () => {
  const { token, isTeacher, teacherId } = getTeacherAuthFromCookie()
  const instituteId = Cookies.get('institute_id')

  if (isTeacher) {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : undefined,
    }
    const instituteNum = Number(instituteId)
    if (Number.isFinite(instituteNum) && instituteNum > 0) {
      headers['institute-Id'] = instituteNum
    }

    let res = await axios.get(BASE_URL + '/teacher-classes', { headers })
    let coursesList = mapCoursesFromTeacherClasses(res.data)

    if (coursesList.length === 0 && teacherId) {
      try {
        res = await axios.get(BASE_URL + `/teacher-classes/${teacherId}`, {
          headers,
        })
        coursesList = mapCoursesFromTeacherClasses(res.data)
      } catch (error) {
        console.error('Error fetching teacher-classes by id:', error)
      }
    }

    // If assigned classes only have course names, resolve ids from /course/all
    if (coursesList.length === 0) {
      try {
        const courseRes = await ApiEndpoints.getAllCourses()
        const allCourses = courseRes?.data?.data ?? []
        const raw = res.data?.data ?? res.data
        const assigned = Array.isArray(raw)
          ? raw.flatMap((item) =>
              item?.assigned_classes ?? item?.assignedClasses ?? item?.classes ?? [item],
            )
          : raw?.assigned_classes ?? raw?.assignedClasses ?? raw?.classes ?? []
        const names = new Set(
          (Array.isArray(assigned) ? assigned : [])
            .map(
              (item) =>
                item?.course_name ??
                item?.courseName ??
                (typeof item?.course === 'string' ? item.course : item?.course?.name),
            )
            .filter(Boolean)
            .map((n) => String(n).toLowerCase()),
        )
        if (names.size > 0 && Array.isArray(allCourses)) {
          return allCourses.filter((c) =>
            names.has(String(c?.name || '').toLowerCase()),
          )
        }
      } catch (error) {
        console.error('Error resolving teacher courses:', error)
      }
    }

    return coursesList
  }

  const res = await ApiEndpoints.getAllCourses()
  return res.data.data
}

export default function ViewAttendance() {
  const VIEW_ATTENDANCE_STATE_KEY = 'viewAttendancePageState'
  const restoredStateRef = useRef(null)
  if (restoredStateRef.current === null && typeof window !== 'undefined') {
    try {
      const rawState = sessionStorage.getItem(VIEW_ATTENDANCE_STATE_KEY)
      restoredStateRef.current = rawState ? JSON.parse(rawState) : {}
    } catch (error) {
      restoredStateRef.current = {}
    }
  }
  const restoredState = restoredStateRef.current || {}
  const { logout, user } = useAuth()
  const { isCollapsed } = useSidebar()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const { logo, name, id } = useInstitute();
  const [showFilterCard, setShowFilterCard] = useState(
    restoredState.showFilterCard ?? true,
  )
  const [openTable, setOpenTable] = useState(restoredState.openTable ?? false)
  const institute_id = Cookies.get('institute_id')


  const { student_id, course_id, class_id } = useParams()
  console.log(
    'STUDENT ID:',
    student_id,
    'COURSE ID: ',
    course_id,
    'CLASS_ID:',
    class_id,
  )
  const [courses, setCourses] = useState(restoredState.courses || [])
  const [classes, setClasses] = useState(restoredState.classes || [])
  const [allStudents, setAllStudents] = useState(restoredState.allStudents || [])
  const [filteredStudents, setFilteredStudents] = useState(
    restoredState.filteredStudents || [],
  )
  const [selectedStudent, setSelectedStudent] = useState(
    restoredState.selectedStudent || null,
  )
  const [selectedData, setSelectedData] = useState(restoredState.selectedData || [])
  const [allTeachers, setAllTeachers] = useState([])
  const [attendanceData, setAttendanceData] = useState(
    restoredState.attendanceData || [],
  )
  const [selectedCourseId, setSelectedCourseId] = useState(
    restoredState.selectedCourseId ?? '',
  )
  const [selectedClassId, setSelectedClassId] = useState(
    restoredState.selectedClassId ?? '',
  )
  const [admissionNo, setAdmissionNo] = useState('')
  const [rollNumberByStudentId, setRollNumberByStudentId] = useState({})
  const tablePdfRef = useRef(null)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const [fromDate, setFromDate] = useState(
    restoredState.fromDate ? new Date(restoredState.fromDate) : null,
  )
  const [toDate, setToDate] = useState(
    restoredState.toDate ? new Date(restoredState.toDate) : null,
  )
  let initialAttendanceStatusFilter = restoredState.attendanceStatusFilter || 'all'
  try {
    const userCookie = Cookies.get('user')
    if (userCookie && JSON.parse(userCookie)?.user_type === 'teacher') {
      initialAttendanceStatusFilter = 'all'
    }
  } catch (_) {
    /* keep initialAttendanceStatusFilter */
  }
  const [attendanceStatusFilter, setAttendanceStatusFilter] = useState(
    initialAttendanceStatusFilter,
  )
  const navigate = useNavigate()
  const normalizedUserType = (user?.user_type || '').toLowerCase()
  const normalizedRoles = (user?.roles || []).map((role) => String(role).toLowerCase())
  const isAdminUser = normalizedUserType === 'admin' || normalizedRoles.includes('admin')

  useEffect(() => {
    if (allStudents.length > 0 && student_id && course_id && class_id) {
      const urlStudentId = Number(student_id)
      const urlCourseId = Number(course_id)
      const urlClassId = Number(class_id)

      if (
        !Number.isFinite(urlStudentId) ||
        !Number.isFinite(urlCourseId) ||
        !Number.isFinite(urlClassId)
      ) {
        return
      }

      console.log('✅ useEffect triggered with params:')
      console.log('All Students:', allStudents)
      console.log('student_id:', urlStudentId, 'course_id:', urlCourseId, 'class_id:', urlClassId)

      const student = allStudents.find((s) => Number(s.id) === urlStudentId)
      console.log('Found student:', student)

      const mergedStudent = student
        ? { ...student, course_id: urlCourseId, class_id: urlClassId }
        : {
            id: urlStudentId,
            course_id: urlCourseId,
            class_id: urlClassId,
            name: 'Student',
          }

      setSelectedStudent(mergedStudent)
      setSelectedCourseId(urlCourseId)
      setSelectedClassId(urlClassId)

      ApiEndpoints.getAllClassesByCourseId(urlCourseId)
        .then((res) => {
          console.log('Classes fetched for course:', res.data.data)
          setClasses(res.data.data || [])
        })
        .catch((err) => {
          console.error('Classes fetch failed:', err?.response?.data?.message || err)
        })

      ApiEndpoints.getAllAttendance({
        class_id: urlClassId,
        student_id: urlStudentId,
      })
        .then(async (res) => {
          console.log('Attendance data fetched:', res.data.attendance)

          const filteredAttendance = (res.data.attendance || []).filter(
            (entry) => Number(entry.student_id) === urlStudentId,
          )

          console.log('✅ Filtered attendance for student:', filteredAttendance)

          const enrichedAttendance = await enrichStudentAttendanceWithHolidays({
            attendanceList: filteredAttendance,
            students: [mergedStudent],
            selectedStudentId: urlStudentId,
            topLevelHolidayTitle: res.data?.holiday_title || '',
          })

          setAttendanceData(enrichedAttendance)
          setSelectedData(enrichedAttendance)
          setOpenTable(true)
        })
        .catch((err) => {
          console.error('Attendance fetch failed:', err?.response?.data || err)
          toast.error(
            err?.response?.data?.message ||
              err?.response?.data?.errors?.class_id?.[0] ||
              'Failed to load attendance.',
          )
        })

      const filtered = allStudents.filter(
        (s) => Number(s.course_id) === urlCourseId,
      )
      console.log('Filtered students by course:', filtered)
      setFilteredStudents(filtered)
    }
  }, [allStudents, student_id, course_id, class_id])

  useEffect(() => {
    if (fromDate && toDate) {
      const filtered = attendanceData.filter((item) => {
        const itemDate = new Date(item.date)
        return itemDate >= fromDate && itemDate <= toDate
      })
      setSelectedData(filtered)
    } else {
      setSelectedData(attendanceData)
    }
  }, [fromDate, toDate, attendanceData])

  useEffect(() => {
    const { isTeacher } = getTeacherAuthFromCookie()
    // Teachers must always load from teacher-classes (ignore session-restored courses).
    if (!isTeacher && courses.length > 0) return

    let cancelled = false
    fetchCoursesForViewAttendance()
      .then((courseList) => {
        if (!cancelled && Array.isArray(courseList)) {
          setCourses(courseList)
        }
      })
      .catch(console.error)

    return () => {
      cancelled = true
    }
    // Intentionally run once on mount (same as TakeAttendance).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (allStudents.length > 0) return
    const institueId = Cookies.get('institute_id')
    const userCookie = Cookies.get('user')

    let token = null
    if (userCookie) {
      try {
        const user = JSON.parse(userCookie)
        token = user.token
        console.log('TOKEN:', token)
      } catch (error) {
        console.error('Failed to parse user cookie', error)
      }
    }
    const fetchFilteredData = async () => {
      try {
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'institute-Id': institueId || institute_id,
        }

        const response = await axios.get(
          BASE_URL+'/student/all',
          { headers },
        )
        console.log('Response of all students:', response.data.data)
        setAllStudents(response.data.data)
      } catch (error) {
        console.error(error.response?.data?.message || 'Something wents wrong.')
      }
    }
    fetchFilteredData()
  }, [allStudents.length, institute_id])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const stateToPersist = {
      showFilterCard,
      openTable,
      courses,
      classes,
      allStudents,
      filteredStudents,
      selectedStudent,
      selectedData,
      attendanceData,
      selectedCourseId,
      selectedClassId,
      fromDate: fromDate ? fromDate.toISOString() : null,
      toDate: toDate ? toDate.toISOString() : null,
      attendanceStatusFilter,
    }

    sessionStorage.setItem(VIEW_ATTENDANCE_STATE_KEY, JSON.stringify(stateToPersist))
  }, [
    showFilterCard,
    openTable,
    courses,
    classes,
    allStudents,
    filteredStudents,
    selectedStudent,
    selectedData,
    attendanceData,
    selectedCourseId,
    selectedClassId,
    fromDate,
    toDate,
    attendanceStatusFilter,
  ])
  const handleCourseChange = async (e) => {
    const courseId = Number(e.target.value)
    setSelectedCourseId(courseId)

    // Reset dependent data
    setClasses([])
    setFilteredStudents([])
    setSelectedStudent(null)

    if (courseId) {
      try {
        const res = await ApiEndpoints.getAllClassesByCourseId(courseId)
        const classList = res.data.data || []
        setClasses(classList)

        // Filter students for this course
        const filtered = allStudents.filter((s) => s.course_id === courseId)
        setFilteredStudents(filtered)
      } catch (error) {
        toast.error('Error fetching classes or students.')
      }
    }
  }

  const handleRefresh = async () => {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(VIEW_ATTENDANCE_STATE_KEY)
    }
    // Reset selected student, class, and attendance data
    setSelectedStudent(null)
    setSelectedClassId('')
    setSelectedCourseId('')
    setFilteredStudents([])
    setSelectedData([])
    setOpenTable(false)
    setFromDate(null)
    setToDate(null)
    setAttendanceStatusFilter('all')
    setRollNumberByStudentId({})
    navigate('/ViewAttendance')
    // Refetch courses
    try {
      const courseList = await fetchCoursesForViewAttendance()
      setCourses(courseList) // Set fetched courses
    } catch (error) {
      console.error('Error fetching courses:', error)
    }

    // Optionally, you can also refetch all students, classes, etc., as needed.
    try {
      const institueId = Cookies.get('institute_id')
      const userCookie = Cookies.get('user')
      let token = null
      if (userCookie) {
        try {
          const user = JSON.parse(userCookie)
          token = user.token
        } catch (error) {
          console.error('Failed to parse user cookie', error)
        }
      }

      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'institute-Id': Number(institueId) || institute_id,
      }

      const response = await axios.get(
        BASE_URL+'/student/all',
        { headers },
      )
      setAllStudents(response.data.data)
    } catch (error) {
      console.error(error.response?.data?.message || 'Something went wrong.')
    }
  }

  const formatAttendanceDate = (date) => {
    if (!date) return 'N/A'

    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    })
  }

  const formatAttendanceStatus = (status) => {
    if (!status) return 'N/A'

    return String(status)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase())
  }

  const getMatchedStudent = (attendance) =>
    allStudents.find(
      (student) =>
        Number(student.id) === Number(attendance.student_id) ||
        student.name === attendance.student_name,
    )

  const getStudentAdmissionNumber = (attendance) =>
    attendance?.admission_number ||
    getMatchedStudent(attendance)?.admission_number ||
    'N/A'

  const getStudentRollNumber = (attendance) => {
    const matchedStudent = getMatchedStudent(attendance)
    const roll =
      attendance?.roll_number ||
      attendance?.roll_no ||
      rollNumberByStudentId[Number(attendance?.student_id)] ||
      matchedStudent?.roll_number ||
      matchedStudent?.roll_no ||
      matchedStudent?.rollNumber ||
      ''
    return roll || '-'
  }

  const compareRollNumbers = (rollA, rollB) => {
    const a = !rollA || rollA === '-' ? '' : String(rollA).trim()
    const b = !rollB || rollB === '-' ? '' : String(rollB).trim()
    if (!a && !b) return 0
    if (!a) return 1
    if (!b) return -1
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
  }

  const sortAttendanceByRollNumber = (rows = []) =>
    [...rows].sort((left, right) =>
      compareRollNumbers(getStudentRollNumber(left), getStudentRollNumber(right)),
    )

  useEffect(() => {
    let cancelled = false
    const classId = selectedClassId || class_id
    const studentIds = [
      ...new Set(
        (attendanceData || [])
          .map((row) => Number(row.student_id))
          .filter((id) => Number.isFinite(id) && id > 0),
      ),
    ]

    if (!classId || studentIds.length === 0) {
      setRollNumberByStudentId({})
      return undefined
    }

    const loadRollNumbers = async () => {
      const userCookie = Cookies.get('user')
      let authToken = null
      if (userCookie) {
        try {
          authToken = JSON.parse(userCookie)?.token || null
        } catch {
          authToken = null
        }
      }
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {}

      const entries = await Promise.all(
        studentIds.map(async (studentId) => {
          try {
            const response = await axios.get(
              BASE_URL + '/get/student/all/enroll',
              {
                params: { student_id: studentId },
                headers,
              },
            )
            const enrollments = Array.isArray(response?.data?.data)
              ? response.data.data
              : []
            const classMatch = enrollments.find(
              (row) => Number(row.class_id) === Number(classId),
            )
            const fallback = enrollments[0]
            const roll =
              classMatch?.roll_number ||
              classMatch?.roll_no ||
              fallback?.roll_number ||
              fallback?.roll_no ||
              ''
            return [studentId, roll]
          } catch {
            return [studentId, '']
          }
        }),
      )

      if (!cancelled) {
        setRollNumberByStudentId(Object.fromEntries(entries))
      }
    }

    loadRollNumbers()
    return () => {
      cancelled = true
    }
  }, [selectedClassId, class_id, attendanceData])

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')

  const handleDownloadPdf = async () => {
    if (!tablePdfRef.current || !openTable) {
      toast.error('No attendance table available to download.')
      return
    }

    if (!statusFilteredData || statusFilteredData.length === 0) {
      toast.error('No attendance data available to download.')
      return
    }

    setIsGeneratingPdf(true)
    let pdfWrapper = null

    try {
      const instituteNameFromCookie =
        name || Cookies.get('name') || Cookies.get('institute_name') || 'Institute'
      const selectedCourseName =
        courses.find((course) => Number(course.id) === Number(selectedCourseId))?.name ||
        'All'
      const selectedClassName =
        classes.find((cls) => Number(cls.id) === Number(selectedClassId))?.class_name ||
        'All'
      const selectedStudentName = selectedStudent?.name
        ? selectedStudent.name.trim().replace(/\s+/g, '_')
        : 'attendance'
      const filename = `${selectedStudentName}_attendance_${new Date().toISOString().split('T')[0]}.pdf`
      const instituteLogoUrl =
        Cookies.get('institute_logo') ||
        (logo && String(logo).trim() !== '' && String(logo).toLowerCase() !== 'no_image.png'
          ? logo
          : '')
      let instituteWatermarkData = null

      if (instituteLogoUrl) {
        try {
          const instituteLogoRawData = await fetchImageAsDataUrlForPdf(instituteLogoUrl)
          instituteWatermarkData = await makeWatermarkImageDataUrl(instituteLogoRawData)
        } catch {
          instituteWatermarkData = null
        }
      }

      const options = {
        margin: [10, 10, 10, 10],
        filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
        pagebreak: { mode: ['css', 'legacy'] },
      }

      pdfWrapper = document.createElement('div')
      pdfWrapper.style.position = 'fixed'
      pdfWrapper.style.left = '-9999px'
      pdfWrapper.style.top = '0'
      pdfWrapper.style.width = '1120px'
      pdfWrapper.style.padding = '16px'
      pdfWrapper.style.backgroundColor = '#ffffff'
      pdfWrapper.style.fontFamily = 'Inter, Arial, sans-serif'

      const printableContent = document.createElement('div')
      printableContent.style.position = 'relative'
      printableContent.style.backgroundColor = '#ffffff'
      printableContent.style.padding = '8px'

      const contentWrapper = document.createElement('div')
      contentWrapper.style.position = 'relative'
      contentWrapper.style.zIndex = '2'

      const instituteHeading = document.createElement('h2')
      instituteHeading.textContent = instituteNameFromCookie
      instituteHeading.style.margin = '0 0 8px 0'
      instituteHeading.style.textAlign = 'center'
      instituteHeading.style.fontSize = '24px'
      instituteHeading.style.fontWeight = '700'
      instituteHeading.style.color = '#111827'

      const reportHeading = document.createElement('h3')
      reportHeading.textContent = 'Attendance Report'
      reportHeading.style.margin = '0 0 12px 0'
      reportHeading.style.textAlign = 'center'
      reportHeading.style.fontSize = '18px'
      reportHeading.style.fontWeight = '700'
      reportHeading.style.color = '#223843'

      const selectionMeta = document.createElement('div')
      selectionMeta.innerHTML = `
        <span><strong>Course:</strong> ${escapeHtml(selectedCourseName)}</span>
        <span><strong>Class:</strong> ${escapeHtml(selectedClassName)}</span>
        <span><strong>Student:</strong> ${escapeHtml(selectedStudent?.name || 'All')}</span>
        <span><strong>Status:</strong> ${escapeHtml(formatAttendanceStatus(attendanceStatusFilter))}</span>
        <span><strong>Date Range:</strong> ${
          fromDate || toDate
            ? escapeHtml(`${fromDate ? formatAttendanceDate(fromDate) : 'Start'} to ${toDate ? formatAttendanceDate(toDate) : 'End'}`)
            : 'All'
        }</span>
      `
      selectionMeta.style.display = 'flex'
      selectionMeta.style.flexWrap = 'wrap'
      selectionMeta.style.justifyContent = 'center'
      selectionMeta.style.gap = '8px 18px'
      selectionMeta.style.margin = '0 0 16px 0'
      selectionMeta.style.fontSize = '13px'
      selectionMeta.style.color = '#374151'

      const tableContainer = document.createElement('div')
      tableContainer.style.position = 'relative'
      tableContainer.style.minHeight = '320px'

      if (instituteWatermarkData) {
        const watermarkImage = document.createElement('img')
        watermarkImage.src = instituteWatermarkData
        watermarkImage.alt = 'Institute watermark'
        watermarkImage.style.position = 'absolute'
        watermarkImage.style.top = '55%'
        watermarkImage.style.left = '50%'
        watermarkImage.style.transform = 'translate(-50%, -50%)'
        watermarkImage.style.width = '320px'
        watermarkImage.style.height = '320px'
        watermarkImage.style.objectFit = 'cover'
        watermarkImage.style.borderRadius = '50%'
        watermarkImage.style.opacity = '0.18'
        watermarkImage.style.pointerEvents = 'none'
        watermarkImage.style.zIndex = '1'
        tableContainer.appendChild(watermarkImage)
      }

      const printableTable = document.createElement('table')
      printableTable.style.width = '100%'
      printableTable.style.borderCollapse = 'collapse'
      printableTable.style.position = 'relative'
      printableTable.style.zIndex = '2'
      printableTable.style.backgroundColor = 'rgba(255, 255, 255, 0.92)'

      printableTable.innerHTML = `
        <thead>
          <tr style="background-color: #F3F4F6;">
            <th style="padding: 10px; border: 1px solid #D1D3D4; text-align: left; font-size: 12px;">Admission Number</th>
            <th style="padding: 10px; border: 1px solid #D1D3D4; text-align: left; font-size: 12px;">Roll Number</th>
            <th style="padding: 10px; border: 1px solid #D1D3D4; text-align: left; font-size: 12px;">Student Name</th>
            <th style="padding: 10px; border: 1px solid #D1D3D4; text-align: left; font-size: 12px;">Date</th>
            <th style="padding: 10px; border: 1px solid #D1D3D4; text-align: left; font-size: 12px;">Status</th>
            <th style="padding: 10px; border: 1px solid #D1D3D4; text-align: left; font-size: 12px;">Remarks</th>
          </tr>
        </thead>
        <tbody>
          ${statusFilteredData
            .map(
              (att) => `
                <tr>
                  <td style="padding: 9px; border: 1px solid #E5E7EB; font-size: 12px;">${escapeHtml(getStudentAdmissionNumber(att))}</td>
                  <td style="padding: 9px; border: 1px solid #E5E7EB; font-size: 12px;">${escapeHtml(getStudentRollNumber(att))}</td>
                  <td style="padding: 9px; border: 1px solid #E5E7EB; font-size: 12px;">${escapeHtml(att.student_name || '-')}</td>
                  <td style="padding: 9px; border: 1px solid #E5E7EB; font-size: 12px;">${escapeHtml(formatAttendanceDate(att.date))}</td>
                  <td style="padding: 9px; border: 1px solid #E5E7EB; font-size: 12px; font-weight: 700;">${escapeHtml(formatAttendanceStatus(att.status))}</td>
                  <td style="padding: 9px; border: 1px solid #E5E7EB; font-size: 12px;">${escapeHtml(att.remarks || '-')}</td>
                </tr>
              `,
            )
            .join('')}
        </tbody>
      `

      tableContainer.appendChild(printableTable)
      contentWrapper.appendChild(instituteHeading)
      contentWrapper.appendChild(reportHeading)
      contentWrapper.appendChild(selectionMeta)
      contentWrapper.appendChild(tableContainer)
      printableContent.appendChild(contentWrapper)
      pdfWrapper.appendChild(printableContent)
      document.body.appendChild(pdfWrapper)

      await html2pdf().set(options).from(printableContent).save()
    } catch {
      toast.error('Failed to generate PDF. Please try again.')
    } finally {
      if (pdfWrapper?.parentNode) {
        pdfWrapper.parentNode.removeChild(pdfWrapper)
      }
      setIsGeneratingPdf(false)
    }
  }

  const handleClassChange = async (e) => {
    const classId = e.target.value
    setSelectedClassId(classId)
    setSelectedStudent(null)

    console.log('Selected Class ID:', classId)

    // Filter students by class
    const filtered = allStudents.filter(
      (student) => student.class_id === Number(classId),
    )
    setFilteredStudents(filtered)

    try {
      const payload = { class_id: parseInt(classId) }
      console.log('Fetching attendance with payload:', payload)

      const res = await ApiEndpoints.getAllAttendance(payload)
      const attendanceList = res.data.attendance || []
      const enrichedAttendance = await enrichStudentAttendanceWithHolidays({
        attendanceList,
        students: filtered,
        selectedStudentId: null,
        topLevelHolidayTitle: res.data?.holiday_title || '',
      })
      setAttendanceData(enrichedAttendance)
      console.log('Attendance by Class:', enrichedAttendance)
      setSelectedData(enrichedAttendance)
    } catch (error) {
      console.error(
        'Error fetching attendance by class:',
        error.response?.data?.message,
      )
    }

    setOpenTable(true)
  }
  const handleStudentChange = async (e) => {
    const studentId = Number(e.target.value)
    const student = filteredStudents.find((s) => s.id === studentId)

    if (student) {
      setSelectedStudent(student)
      setSelectedCourseId(student.course_id) // sync course dropdown
      // setSelectedClassId(student.class_id)
      // Fetch classes for course if not already fetched
      if (!classes.length) {
        const res = await ApiEndpoints.getAllClassesByCourseId(
          student.course_id,
        )
        setClasses(res.data.data || [])
      }

      setOpenTable(true)

      try {
        const res = await ApiEndpoints.getAllAttendance({
          class_id: student.class_id,
          student_id: student.id,
        })
        const filteredAttendance = (res.data.attendance || []).filter(
          (entry) => entry.student_id === student.id,
        )
        const enrichedAttendance = await enrichStudentAttendanceWithHolidays({
          attendanceList: filteredAttendance,
          students: [student],
          selectedStudentId: student.id,
          topLevelHolidayTitle: res.data?.holiday_title || '',
        })
        setAttendanceData(enrichedAttendance)
        setSelectedData(enrichedAttendance)
      } catch (error) {
        console.error(
          'Error fetching student attendance:',
          error.response?.data?.message,
        )
      }
    }
  }

  useEffect(() => {
    if (fromDate && toDate) {
      const filtered = attendanceData.filter((item) => {
        const itemDate = new Date(item.date)
        return itemDate >= fromDate && itemDate <= toDate
      })
      setSelectedData(filtered)
    } else {
      setSelectedData(attendanceData)
    }
  }, [fromDate, toDate, attendanceData])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1

        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }

      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const statusFilteredData = sortAttendanceByRollNumber(
    attendanceStatusFilter === 'all'
      ? selectedData || []
      : (selectedData || []).filter(
          (att) =>
            String(att?.status || '').toLowerCase() === attendanceStatusFilter,
        ),
  )

  return (
    <>
      <style>
        {`
          .filter-card-container {
            overflow: visible !important;
            position: relative !important;
            z-index: 10 !important;
          }
          .table td {
            user-select: text;
          }
          .table tbody tr {
            cursor: default;
          }
        `}
      </style>
      <div 
        style={{ 
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        {/* Header */}
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
        <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
          <div 
            className="d-flex justify-content-between align-items-center"
            style={{
              background: '#F3F4F6',
              paddingLeft: '28px',
              paddingRight: '28px',
              paddingTop: '12px',
              paddingBottom: '12px',
              overflow: 'visible',
              borderBottom: '1px solid #D1D3D4',
              width: '100%',
            }}
          >
            {/* Left: Title */}
            <div className="d-flex align-items-center gap-3">
              {/* School Logo */}
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                overflow: 'hidden', 
                background: '#223843',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                flexShrink: 0,
              }}>
                {!logoError ? (
                  <img 
                    src={logo} 
                    alt="School Logo" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                )}
              </div>
              
              <h3 
                className="m-0 fw-bold" 
                style={{ 
                  letterSpacing: '-0.02em',
                  overflow: 'visible',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 'clamp(18px, 2.5vw, 22px)',
                  color: '#223843',
                  lineHeight: '1.2',
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                {selectedStudent ? (
                  <>
                    View{' '}
                    <span style={{ color: '#223843' }}>
                      {selectedStudent.name.charAt(0).toUpperCase() +
                        selectedStudent.name.slice(1).toLowerCase()}
                    </span>
                    's Attendance
                  </>
                ) : (
                  'View Attendance'
                )}
              </h3>
            </div>
            
            {/* Right: Profile Icon with Dropdown */}
            {!isMobile && (
              <ProfileDropdown logout={logout} />
            )}
          </div>
        </div>
        </div>
        <div
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '100vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px',
          }}
        >
          {/* Filter Toggle Button */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center gap-3">
              <button
                onClick={() => setShowFilterCard(!showFilterCard)}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: showFilterCard ? '#ffffff' : '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = showFilterCard ? '#223843' : '#223843'
                  e.currentTarget.style.color = '#ffffff'
                  e.currentTarget.style.borderColor = showFilterCard ? '#223843' : '#223843'
                  e.currentTarget.style.transform = 'scale(1.05)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = showFilterCard ? '#223843' : '#ffffff'
                  e.currentTarget.style.color = showFilterCard ? '#ffffff' : '#223843'
                  e.currentTarget.style.borderColor = '#D1D3D4'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
                title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
              >
                <BsFunnelFill size={20} />
              </button>
            </div>
          </div>

          {/* Modern Filter Section */}
          <div 
            className="filter-card-container"
            style={{
              maxHeight: showFilterCard ? '1000px' : '0',
              overflow: showFilterCard ? 'visible' : 'hidden',
              transition: 'max-height 0.3s ease-in-out',
              marginBottom: showFilterCard ? '24px' : '0',
              position: 'relative',
              zIndex: showFilterCard ? 10 : 1,
            }}
          >
            {showFilterCard && (
              <Card 
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  overflow: 'visible',
                  position: 'relative',
                }}
              >
                <div 
                  style={{
                    background: '#ffffff',
                    padding: '20px',
                    borderBottom: '1px solid #F3F4F6',
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                      <div 
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <BsSearch style={{ color: '#ffffff', fontSize: '18px' }} />
                      </div>
                      <div>
                        <h5 className="m-0 fw-semibold text-start" style={{ color: '#111827', fontSize: '16px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Select Course, Class & Student
                        </h5>
                        <p className="m-0 mt-1" style={{fontSize: '13px', color: '#6b7280' }}>
                          Choose course, class, and student to view attendance
                        </p>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        onClick={handleDownloadPdf}
                        disabled={!openTable || isGeneratingPdf || statusFilteredData.length === 0}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          backgroundColor:
                            openTable && !isGeneratingPdf && statusFilteredData.length > 0
                              ? '#2563EB'
                              : '#9CA3AF',
                          border: 'none',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor:
                            openTable && !isGeneratingPdf && statusFilteredData.length > 0
                              ? 'pointer'
                              : 'not-allowed',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          if (!openTable || isGeneratingPdf || statusFilteredData.length === 0) return
                          e.currentTarget.style.backgroundColor = '#1D4ED8'
                          e.currentTarget.style.transform = 'scale(1.05)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor =
                            openTable && !isGeneratingPdf && statusFilteredData.length > 0
                              ? '#2563EB'
                              : '#9CA3AF'
                          e.currentTarget.style.transform = 'scale(1)'
                        }}
                        title={isGeneratingPdf ? 'Generating PDF...' : 'Download Attendance PDF'}
                      >
                        <BsFiletypePdf size={18} />
                      </button>
                      <button
                        onClick={handleRefresh}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          backgroundColor: '#10B981',
                          border: 'none',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#059669'
                          e.currentTarget.style.transform = 'scale(1.05)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#10B981'
                          e.currentTarget.style.transform = 'scale(1)'
                        }}
                        title="Refresh"
                      >
                        <BsArrowClockwise size={20} />
                      </button>
                    </div>
                  </div>
                </div>

                <Card.Body className="p-4" style={{ backgroundColor: '#ffffff', overflow: 'visible', position: 'relative' }}>
                  <Row className="g-3 text-start">
                    <Col xs={12} md={4}>
                      <div style={{ position: 'relative' }}>
                        <label className="fw-semibold mb-2 d-block" style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Select Course
                        </label>
                        <Typeahead
                          id="course-select"
                          labelKey="name"
                          onChange={(selected) => {
                            const course = selected[0]
                            if (course) {
                              handleCourseChange({ target: { value: course.id } })
                            } else {
                              handleCourseChange({ target: { value: '' } })
                            }
                          }}
                          options={courses}
                          placeholder="Select Course"
                          selected={courses.filter(
                            (cs) => cs.id === Number(selectedCourseId),
                          )}
                          inputProps={{
                            style: {
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                              transition: 'all 0.2s ease',
                              backgroundColor: '#ffffff',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }
                          }}
                        />
                      </div>
                    </Col>
                    <Col xs={12} md={4}>
                      <div style={{ position: 'relative' }}>
                        <label className="fw-semibold mb-2 d-block" style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Select Class
                        </label>
                        <Typeahead
                          id="class-select"
                          labelKey="class_name"
                          onChange={(selected) => {
                            const cls = selected[0]
                            if (cls) {
                              handleClassChange({ target: { value: cls.id } })
                            } else {
                              handleClassChange({ target: { value: '' } })
                            }
                          }}
                          options={classes}
                          placeholder="Select Class"
                          selected={classes.filter(
                            (cs) => cs.id === Number(selectedClassId),
                          )}
                          inputProps={{
                            style: {
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                              transition: 'all 0.2s ease',
                              backgroundColor: '#ffffff',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }
                          }}
                        />
                      </div>
                    </Col>
                    <Col xs={12} md={4}>
                      <div style={{ position: 'relative' }}>
                        <label className="fw-semibold mb-2 d-block" style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Select Student
                        </label>
                        <Typeahead
                          id="student-select"
                          labelKey={(option) =>
                            `${option.name} - ${option.admission_number}`
                          }
                          onChange={(selected) => {
                            const student = selected[0]
                            if (student) {
                              handleStudentChange({ target: { value: student.id } })
                            } else {
                              setSelectedStudent(null)
                            }
                          }}
                          onInputChange={(text) => {
                            if (text === '') {
                              setSelectedStudent(null)
                            }
                          }}
                          options={filteredStudents}
                          placeholder="Select Student"
                          selected={
                            selectedStudent
                              ? filteredStudents.filter((s) => s.id === selectedStudent.id)
                              : []
                          }
                          inputProps={{
                            style: {
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                              transition: 'all 0.2s ease',
                              backgroundColor: '#ffffff',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }
                          }}
                        />
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </div>
          {/* Date Filters */}
          {openTable && (
            <Row className="mb-3 d-flex justify-content-start gap-3 text-start">
              <Col xs={12} md={3}>
                <div style={{ position: 'relative' }}>
                  <label className="fw-semibold mb-2 d-block" style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                    Start Date
                  </label>
                  <BsCalendar3 
                    style={{ 
                      position: 'absolute', 
                      left: '12px', 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      marginTop: '12px',
                      color: '#6b7280',
                      fontSize: '16px',
                      zIndex: 1,
                      pointerEvents: 'none'
                    }} 
                  />
                  <Form.Control
                    type="date"
                    value={
                      fromDate instanceof Date && !isNaN(fromDate)
                        ? fromDate.toISOString().split('T')[0]
                        : ''
                    }
                    onChange={(e) =>
                      setFromDate(e.target.value ? new Date(e.target.value) : null)
                    }
                    style={{
                      paddingLeft: '40px',
                      borderRadius: '8px',
                      border: '1px solid #D1D3D4',
                      fontSize: '14px',
                      height: '40px',
                      transition: 'all 0.2s ease',
                      backgroundColor: '#ffffff',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#223843'
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#D1D3D4'
                    }}
                  />
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div style={{ position: 'relative' }}>
                  <label className="fw-semibold mb-2 d-block" style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                    End Date
                  </label>
                  <BsCalendar3 
                    style={{ 
                      position: 'absolute', 
                      left: '12px', 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      marginTop: '12px',
                      color: '#6b7280',
                      fontSize: '16px',
                      zIndex: 1,
                      pointerEvents: 'none'
                    }} 
                  />
                  <Form.Control
                    type="date"
                    value={
                      toDate instanceof Date && !isNaN(toDate)
                        ? toDate.toISOString().split('T')[0]
                        : ''
                    }
                    onChange={(e) =>
                      setToDate(e.target.value ? new Date(e.target.value) : null)
                    }
                    style={{
                      paddingLeft: '40px',
                      borderRadius: '8px',
                      border: '1px solid #D1D3D4',
                      fontSize: '14px',
                      height: '40px',
                      transition: 'all 0.2s ease',
                      backgroundColor: '#ffffff',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#223843'
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#D1D3D4'
                    }}
                  />
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div>
                  <label className="fw-semibold mb-2 d-block" style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                    Attendance Status
                  </label>
                  <Form.Select
                    value={attendanceStatusFilter}
                    onChange={(e) => setAttendanceStatusFilter(e.target.value)}
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #D1D3D4',
                      fontSize: '14px',
                      height: '40px',
                      transition: 'all 0.2s ease',
                      backgroundColor: '#ffffff',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    <option value="all">All</option>
                    <option value="present">Present</option>
                    <option value="absent">Absent</option>
                    <option value="leave">Leave</option>
                    <option value="holiday">Holiday</option>
                  </Form.Select>
                </div>
              </Col>
            </Row>
          )}

          {/* Table */}
          {openTable && (
            <Card
              ref={tablePdfRef}
              style={{ border: '1px solid #D1D3D4', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#ffffff' }}
            >
              <Table className="table mb-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                <Thead>
                  <Tr style={{ backgroundColor: '#F3F4F6' }}>
                    <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Admission Number</Th>
                    <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Roll Number</Th>
                    <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Student Name</Th>
                    <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Date</Th>
                    <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Status</Th>
                    <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Remarks</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {statusFilteredData && statusFilteredData.length > 0 ? (
                    statusFilteredData.map((att, index) => {
                      const statusKey = String(att.status || '').toLowerCase()
                      const baseBgColor =
                        statusKey === 'present'
                          ? 'rgba(16, 185, 129, 0.1)'
                          : statusKey === 'absent'
                            ? 'rgba(239, 68, 68, 0.1)'
                            : statusKey === 'holiday'
                              ? 'rgba(245, 158, 11, 0.1)'
                              : index % 2 === 0
                                ? '#ffffff'
                                : 'rgba(243, 244, 246, 0.4)'

                      const statusColor =
                        statusKey === 'present'
                          ? '#10B981'
                          : statusKey === 'absent'
                            ? '#EF4444'
                            : statusKey === 'holiday'
                              ? '#F59E0B'
                              : '#111827'

                      return (
                        <Tr 
                          key={att.id || `${att.student_id}-${att.date}-${index}`}
                          style={{ 
                            backgroundColor: baseBgColor,
                            transition: 'background-color 0.2s ease',
                            borderBottom: '1px solid #E5E7EB',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(37, 99, 235, 0.06)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = baseBgColor
                          }}
                        >
                          <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                            {getStudentAdmissionNumber(att)}
                          </Td>
                          <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                            {getStudentRollNumber(att)}
                          </Td>
                          <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                          {isAdminUser && att.student_id && att.student_name ? (
                            <span
                              role="link"
                              tabIndex={0}
                              onClick={() => navigate(`/student/profile/${att.student_id}`)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  navigate(`/student/profile/${att.student_id}`)
                                }
                              }}
                              style={{
                                color: '#223843',
                                cursor: 'pointer',
                                fontWeight: '600',
                                textDecoration: 'underline',
                              }}
                            >
                              {att.student_name}
                            </span>
                          ) : (
                            att.student_name || '-'
                          )}
                          </Td>
                          <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                            {att.date
                              ? new Date(att.date).toLocaleDateString('en-GB', {
                                  day: '2-digit',
                                  month: 'long',
                                  year: 'numeric',
                                })
                              : 'N/A'}
                          </Td>
                          <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: statusColor, fontWeight: '600', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                            {formatAttendanceStatus(att.status)}
                          </Td>
                          <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{att.remarks || '—'}</Td>
                        </Tr>
                      )
                    })
                  ) : (
                    <Tr>
                      <Td colSpan="6" className="text-center" style={{ padding: '40px', fontSize: '14px', color: '#6b7280', fontFamily: 'Inter, system-ui, sans-serif' }}>
                        No attendance data found.
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Card>
          )}
        </div>
      </div>
    </>
  )
}
